import { cn } from '@/lib/utils';
import { useI18n } from '@/providers/i18n';
import { speechModels } from '@flowmuse/models/speech';
import { Music } from 'lucide-react';

type AudioCollapsedSummaryProps = {
  data: Record<string, any> | undefined;
  className?: string;
};

const inferNameFromUrl = (url: string): string | undefined => {
  try {
    const u = new URL(String(url), typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
    const parts = u.pathname.split('/').filter(Boolean);
    const last = parts[parts.length - 1];
    if (!last) return undefined;
    return decodeURIComponent(last);
  } catch {
    try {
      const s = String(url);
      const last = s.split('?')[0]?.split('#')[0]?.split('/').filter(Boolean).pop();
      return last ? decodeURIComponent(last) : undefined;
    } catch {
      return undefined;
    }
  }
};

const inferSuffixFromName = (name: string): string | undefined => {
  try {
    const base = String(name).trim();
    if (!base) return undefined;
    const lastDot = base.lastIndexOf('.');
    if (lastDot <= 0 || lastDot === base.length - 1) return undefined;
    const ext = base.slice(lastDot + 1).trim().toLowerCase();
    return ext || undefined;
  } catch {
    return undefined;
  }
};

const inferExtFromMime = (mime: string): string | undefined => {
  try {
    const m = String(mime).toLowerCase().trim();
    if (!m) return undefined;
    if (m === 'audio/mpeg' || m === 'audio/mp3') return 'mp3';
    if (m === 'audio/wav' || m === 'audio/x-wav') return 'wav';
    if (m === 'audio/ogg') return 'ogg';
    if (m === 'audio/webm') return 'webm';
    const slash = m.indexOf('/');
    if (slash > 0 && slash < m.length - 1) {
      const subtype = m.slice(slash + 1);
      const cleaned = subtype.replace(/^x-/, '').replace(/^vnd\./, '').replace(/^x\./, '');
      if (/^[a-z0-9.+-]{1,16}$/.test(cleaned)) return cleaned;
    }
  } catch {}
  return undefined;
};

const fmtSec = (n: number) => (Number.isInteger(n) ? `${n}s` : `${Math.round(n * 10) / 10}s`);

const formatMaybeSeconds = (value: unknown): string | undefined => {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return fmtSec(value);
  }
  if (typeof value === 'string' && value.trim()) {
    const raw = value.trim();
    const n = Number(raw);
    if (Number.isFinite(n) && n > 0) return fmtSec(n);
    return raw;
  }
  return undefined;
};

export const AudioCollapsedSummary = ({ data, className }: AudioCollapsedSummaryProps) => {
  const { t } = useI18n();
  const d = (data ?? {}) as any;
  const autoText = t('Auto');

  const modelId = typeof d?.model === 'string' ? d.model.trim() : '';
  const modelLabel = (() => {
    if (!modelId) return undefined;
    try {
      const lbl = (speechModels as any)?.[modelId]?.label;
      if (typeof lbl === 'string' && lbl.trim()) return lbl.trim();
    } catch {}
    return modelId.includes('/') ? modelId.split('/').pop() : modelId;
  })();

  const generatedUrl = (typeof d?.generated?.url === 'string' && d.generated.url) || undefined;
  const url =
    generatedUrl ||
    (typeof d?.content?.url === 'string' && d.content.url) ||
    undefined;
  const mime =
    (typeof d?.generated?.type === 'string' && d.generated.type) ||
    (typeof d?.content?.type === 'string' && d.content.type) ||
    undefined;

  const isGenerated = Boolean(generatedUrl);

  const typeExt =
    (url ? inferSuffixFromName(inferNameFromUrl(url) || '') : undefined) ||
    (mime ? inferExtFromMime(mime) : undefined) ||
    undefined;

  const duration =
    formatMaybeSeconds(d?.durationSeconds) ??
    formatMaybeSeconds(d?.generated?.durationSeconds) ??
    undefined;

  return (
    <div className={cn('flex h-full w-full items-start gap-4 p-3', className)}>
      <div className="h-32 w-32 shrink-0 overflow-hidden rounded-2xl border border-sidebar-border/30 bg-sidebar-accent/30 flex items-center justify-center shadow-inner">
        <Music size={32} className="text-muted-foreground/40" />
      </div>

      <div className="min-w-0 flex-1 flex flex-col h-32">
        <div className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground/30 mb-1">{t('Audio')}</div>
        <div className="text-sm font-bold truncate text-foreground/90">{modelLabel ?? '-'}</div>

        <div className="mt-2 flex flex-1 flex-col text-[11px] leading-snug">
          {/* 中间参数：固定高度容器（3 行占位） */}
          <div className="grid grid-rows-3 gap-1">
            <div className="truncate">
              <span className="text-muted-foreground/60">{t('Duration')}:</span>{' '}
              <span className="text-foreground/80 font-medium">{isGenerated ? (duration ?? autoText) : '-'}</span>
            </div>
            <div className="truncate">
              <span className="text-muted-foreground/60">{t('Type')}:</span>{' '}
              <span className="text-foreground/80 font-medium">{isGenerated ? (typeExt ?? '-') : '-'}</span>
            </div>
            <div className="truncate opacity-0 select-none" aria-hidden="true">
              &nbsp;
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};


